import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import type { IAvatarValue } from '../../../../app/components/AvatarPicker';
import {
  avatarUploadFile,
  profileAvatarFormData,
} from '../../../../app/components/AvatarPicker/avatarUpload';
import { useAppSelector } from '../../../../app/store/hooks';
import avatar1 from '../../../../assets/images/avatars/avatar-man.svg';
import avatar2 from '../../../../assets/images/avatars/avatar-woman.svg';
import { timezoneOptionLabel } from '../../../../helpers/timezone.helper';
import { useGetActiveCategoriesQuery } from '../../../category/infrastructure/api/category.api';
import { useUserProfile } from '../../../user/ui/hooks/useUserProfile';
import { mentorLifecycleStatus } from '../../application/mentorProfileLifecycle';
import {
  toMentorProfileFormValues,
  toUpdateMentorMeRequest,
} from '../../application/mapMentorProfile';
import {
  computeMentorProfileSteps,
  type MentorProfileStepId,
} from '../../application/mentorProfileSteps';
import type { MentorProfileFormValues } from '../../application/types';
import { useGetMyLessonOfferingsQuery } from '../../../lesson/infrastructure/api/lessonOfferings.api';
import {
  useGetMentorMeQuery,
  usePauseMentorMeMutation,
  usePublishMentorMeMutation,
  useResumeMentorMeMutation,
  useUpdateMentorMeMutation,
} from '../../infrastructure/api/mentor.api';

export const MENTOR_PROFILE_STEP_SECTION_IDS: Record<
  MentorProfileStepId,
  string
> = {
  basic: 'mentor-section-basic',
  experience: 'mentor-section-experience',
  skills: 'mentor-section-skills',
  languages: 'mentor-section-languages',
  availability: 'mentor-section-availability',
};

export const EXPERIENCE_YEAR_OPTIONS = Array.from({ length: 30 }, (_, index) => {
  const year = String(index + 1);
  return { value: year, label: year };
});

export const mentorAvatarList = [
  { id: 1, src: avatar1, alt: 'avatar-1' },
  { id: 2, src: avatar2, alt: 'avatar-2' },
];

const defaultAvatar = (): IAvatarValue => ({
  src: mentorAvatarList[0].src,
  type: 'avatar',
  confirmed: true,
});

const optionalUrl = (message: string) =>
  Yup.string().test('url', message, (value) => {
    if (!value || value.trim() === '') return true;
    try {
      // eslint-disable-next-line no-new
      new URL(value);
      return true;
    } catch {
      return false;
    }
  });

export function useMentorProfileForm() {
  const { t } = useTranslation();
  const accessToken = useAppSelector((state) => state.authorization.accessToken);
  const { view, clockLabel } = useUserProfile();
  const { data: mentor, isLoading, isError } = useGetMentorMeQuery(undefined, {
    skip: !accessToken,
  });
  const { data: lessonOfferings = [] } = useGetMyLessonOfferingsQuery(undefined, {
    skip: !accessToken,
  });
  const [updateMentorMe, { isLoading: isSaving }] = useUpdateMentorMeMutation();
  const [publishMentorMe, { isLoading: isPublishing }] =
    usePublishMentorMeMutation();
  const [pauseMentorMe, { isLoading: isPausing }] = usePauseMentorMeMutation();
  const [resumeMentorMe, { isLoading: isResuming }] =
    useResumeMentorMeMutation();
  const { data: categories = [], isLoading: isCategoriesLoading } =
    useGetActiveCategoriesQuery(undefined, { skip: !accessToken });

  const [avatar, setAvatar] = useState<IAvatarValue>(defaultAvatar);
  const [initialAvatar, setInitialAvatar] = useState<IAvatarValue>(
    defaultAvatar(),
  );
  const [isEditingPhoto, setIsEditingPhoto] = useState(false);
  const [activeStepId, setActiveStepId] =
    useState<MentorProfileStepId>('basic');
  const initialSnapshot = useRef<MentorProfileFormValues | null>(null);

  const validationSchema = useMemo(
    () =>
      Yup.object({
        displayName: Yup.string()
          .trim()
          .min(2, t('form.required', { ns: 'validation' }))
          .max(120)
          .required(t('form.required', { ns: 'validation' })),
        headline: Yup.string().max(255),
        description: Yup.string(),
        goals: Yup.string(),
        introVideoUrl: optionalUrl(t('form.invalid_url', { ns: 'validation' })),
        timezone: Yup.string().max(64),
        nativeLanguages: Yup.array().of(Yup.string()),
        categoryIds: Yup.array().of(Yup.string()),
        experienceYears: Yup.string().test(
          'years',
          t('mentor-profile.invalid_years'),
          (value) => {
            if (!value || value.trim() === '') return true;
            const n = Number(value);
            return Number.isInteger(n) && n >= 1 && n <= 30;
          },
        ),
      }),
    [t],
  );

  const form = useFormik<MentorProfileFormValues>({
    enableReinitialize: true,
    initialValues: toMentorProfileFormValues(mentor),
    validationSchema,
    onSubmit: async (values) => {
      if (isSaving) return;
      if (avatar.type === 'photo' && !avatar.confirmed) return;

      const file = await avatarUploadFile(avatar);
      const request = toUpdateMentorMeRequest({
        ...values,
        avatarUrl: file ? '' : values.avatarUrl,
      });
      await updateMentorMe(
        file
          ? profileAvatarFormData(
              request.profile ?? {},
              request.categoryIds ?? [],
              file,
              avatar.type,
            )
          : request,
      ).unwrap();
      setInitialAvatar(avatar);
      initialSnapshot.current = values;
      setIsEditingPhoto(false);
    },
  });

  useEffect(() => {
    if (!mentor || initialSnapshot.current) return;
    initialSnapshot.current = toMentorProfileFormValues(mentor);
  }, [mentor]);

  const initializeAvatar = useCallback(() => {
    const imageUrl = mentor?.profile?.avatarUrl;
    if (imageUrl) {
      const isPreset = mentorAvatarList.some((item) => item.src === imageUrl);
      const next: IAvatarValue = {
        src: imageUrl,
        type: isPreset ? 'avatar' : 'photo',
        confirmed: true,
      };
      setAvatar(next);
      setInitialAvatar(next);
      return;
    }
    const fallback = defaultAvatar();
    setAvatar(fallback);
    setInitialAvatar(fallback);
  }, [mentor?.profile?.avatarUrl]);

  useEffect(() => {
    initializeAvatar();
  }, [initializeAvatar]);

  const stepsState = useMemo(
    () =>
      computeMentorProfileSteps(form.values, mentor, {
        hasAvatar: Boolean(avatar.src),
        hasStandardPrice: lessonOfferings.length > 0,
      }),
    [form.values, mentor, avatar.src, lessonOfferings.length],
  );

  const isAvatarChanged = avatar.src !== initialAvatar.src;
  const canSubmit =
    form.isValid &&
    (form.dirty || isAvatarChanged) &&
    (avatar.type === 'avatar' || Boolean(avatar.confirmed));
  const canReset = form.dirty || isAvatarChanged;
  const lifecycle = mentorLifecycleStatus(mentor?.status ?? 'draft');
  const isStatusChanging = isPublishing || isPausing || isResuming;

  const onPublish = useCallback(async () => {
    if (lifecycle !== 'draft' || isPublishing || isSaving) return;
    try {
      if ((form.dirty || isAvatarChanged) && form.isValid) {
        await form.submitForm();
      }
      await publishMentorMe().unwrap();
    } catch {
      return;
    }
  }, [
    form,
    isAvatarChanged,
    isPublishing,
    isSaving,
    lifecycle,
    publishMentorMe,
  ]);

  const onPause = useCallback(async () => {
    if (lifecycle !== 'published' || isPausing) return;
    try {
      await pauseMentorMe().unwrap();
    } catch {
      return;
    }
  }, [isPausing, lifecycle, pauseMentorMe]);

  const onResume = useCallback(async () => {
    if (lifecycle !== 'paused' || isResuming) return;
    try {
      await resumeMentorMe().unwrap();
    } catch {
      return;
    }
  }, [isResuming, lifecycle, resumeMentorMe]);

  const onRemoveAvatar = useCallback(() => {
    setAvatar(defaultAvatar());
  }, []);

  const onAvatarChange = useCallback(
    (next: IAvatarValue) => {
      setAvatar(next);
      form.setFieldValue('avatarUrl', next.src);
    },
    [form],
  );

  const scrollToStep = useCallback((id: MentorProfileStepId) => {
    setActiveStepId(id);
    document
      .getElementById(MENTOR_PROFILE_STEP_SECTION_IDS[id])
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const onContinue = useCallback(() => {
    scrollToStep(stepsState.firstIncompleteId ?? 'basic');
  }, [scrollToStep, stepsState.firstIncompleteId]);

  const onCancel = useCallback(() => {
    if (initialSnapshot.current) {
      form.resetForm({ values: initialSnapshot.current });
    } else {
      form.resetForm();
    }
    setAvatar(initialAvatar);
    setIsEditingPhoto(false);
  }, [form, initialAvatar]);

  const displayName =
    form.values.displayName ||
    mentor?.profile?.displayName ||
    view?.displayName ||
    '…';
  const timezoneLabel =
    timezoneOptionLabel(form.values.timezone || mentor?.profile?.timezone) ||
    clockLabel;
  const pictureSrc =
    avatar.src || mentor?.profile?.avatarUrl || view?.avatarUrl;

  return {
    form,
    mentor,
    isLoading,
    isError,
    isSaving,
    canSubmit,
    canReset,
    stepsState,
    activeStepId,
    setActiveStepId,
    scrollToStep,
    onContinue,
    onCancel,
    displayName,
    timezoneLabel,
    avatar,
    isEditingPhoto,
    startPhotoEdit: () => setIsEditingPhoto(true),
    confirmPhoto: () => setIsEditingPhoto(false),
    onAvatarChange,
    onRemoveAvatar,
    pictureSrc,
    gender: view?.gender,
    categories,
    isCategoriesLoading,
    lifecycle,
    isStatusChanging,
    onPublish,
    onPause,
    onResume,
  };
}
